import {
  forwardRef,
  type TextareaHTMLAttributes,
  type MutableRefObject,
  type ForwardedRef,
} from 'react';
import { Input } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useController, useFormContext, type Control } from 'react-hook-form';

export type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> & {
  invalid?: boolean;
};

function bridgeRef(ref: ForwardedRef<HTMLTextAreaElement>) {
  return (node: TextAreaRef | null) => {
    const el = (node?.resizableTextArea?.textArea ?? null) as HTMLTextAreaElement | null;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as MutableRefObject<HTMLTextAreaElement | null>).current = el;
  };
}

const PlainTextarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ invalid, className, rows, ...props }, ref) => {
    const setRef = bridgeRef(ref);
    return (
      <Input.TextArea
        ref={setRef}
        status={invalid ? 'error' : undefined}
        className={className}
        rows={rows ?? 3}
        autoSize={rows ? false : { minRows: 3, maxRows: 10 }}
        {...(props as never)}
      />
    );
  },
);
PlainTextarea.displayName = 'PlainTextarea';

interface ControlledTextareaProps extends TextareaProps {
  name: string;
  control: Control;
}

const ControlledTextarea = forwardRef<HTMLTextAreaElement, ControlledTextareaProps>(
  ({ name, control, invalid, className, rows, ...rest }, ref) => {
    const { field, fieldState } = useController({ name, control });
    const setRef = bridgeRef(ref);
    return (
      <Input.TextArea
        ref={setRef}
        name={field.name}
        value={(field.value ?? '') as string}
        onChange={field.onChange}
        onBlur={field.onBlur}
        status={invalid || fieldState.error ? 'error' : undefined}
        className={className}
        rows={rows ?? 3}
        autoSize={rows ? false : { minRows: 3, maxRows: 10 }}
        {...(rest as never)}
      />
    );
  },
);
ControlledTextarea.displayName = 'ControlledTextarea';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => {
  const ctx = useFormContext();
  if (props.name && ctx?.control) {
    return (
      <ControlledTextarea {...props} name={props.name} control={ctx.control} ref={ref} />
    );
  }
  return <PlainTextarea {...props} ref={ref} />;
});
Textarea.displayName = 'Textarea';
